import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

const DEFAULT_PHRASES = [
  'Understanding what you need...',
  'Finding the right terminology...',
  'Detecting entities...',
  'Creating search terms...',
  'Producing filters...',
  'Adding related terms...',
  'Generating plural and singular forms...',
  'Reviewing the final query...',
];

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(0.8); opacity: 0.55; }
  50% { transform: scale(1.15); opacity: 1; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(2px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  font-size: 14px;
  line-height: 1.4;
  user-select: none;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.theme.orange};
  animation: ${pulse} 1.2s ease-in-out infinite;
  flex: 0 0 auto;
`;

const Phrase = styled.span`
  background: linear-gradient(90deg, #8c8c8c 0%, #262626 50%, #8c8c8c 100%);
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  animation: ${shimmer} 2s linear infinite, ${fadeIn} 220ms ease-out;
  font-weight: 500;
`;

const ThinkingIndicator = ({
  phrases = DEFAULT_PHRASES,
  interval = 3000,
  className,
  style,
}) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (index >= phrases.length - 1) return undefined;
    const id = setTimeout(
      () => setIndex(i => Math.min(i + 1, phrases.length - 1)),
      2000 + interval * Math.random()
    );
    return () => clearTimeout(id);
  }, [index, phrases.length, interval]);

  const current = phrases[index] ?? '';

  return (
    <Container
      className={className}
      style={style}
      role='status'
      aria-live='polite'
    >
      <Dot />
      <Phrase key={index}>{current}</Phrase>
    </Container>
  );
};

ThinkingIndicator.propTypes = {
  phrases: PropTypes.arrayOf(PropTypes.string),
  interval: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.shape({}),
};

export default ThinkingIndicator;
